import React, { useCallback } from 'react'
import { useConfig } from '@peracto/peracto-config'
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3'

export const RECAPTCHA_HEADER = 'g-recaptcha-response'

const useRecaptchaSiteKey = () => useConfig().get('recaptcha.siteKey')

/**
 * Loads Google reCAPTCHA v3 for its children when `recaptcha.siteKey` is set in the
 * Peracto config. Without a site key the children render unwrapped, so sites that have
 * not configured reCAPTCHA see no change in behaviour.
 */
export const RecaptchaProvider = ({ children }) => {
    const siteKey = useRecaptchaSiteKey()

    if (!siteKey) return <>{children}</>

    return <GoogleReCaptchaProvider reCaptchaKey={siteKey}>{children}</GoogleReCaptchaProvider>
}

/**
 * Returns `getRecaptchaHeaders(action)`, which resolves to the headers the API's
 * RecaptchaSubscriber expects, or an empty object when reCAPTCHA is not configured or a
 * token could not be generated. The backend only enforces endpoints listed in
 * `peracto_user.recaptcha.endpoints`, so both halves need configuring for protection.
 *
 * Must be used inside a RecaptchaProvider.
 */
export const useRecaptcha = () => {
    const siteKey = useRecaptchaSiteKey()
    const { executeRecaptcha } = useGoogleReCaptcha()

    const getRecaptchaHeaders = useCallback(
        async action => {
            if (!siteKey) return {}

            if (!executeRecaptcha) {
                console.warn('reCAPTCHA has not loaded yet, submitting without a token')
                return {}
            }

            try {
                const token = await executeRecaptcha(action)
                return token ? { [RECAPTCHA_HEADER]: token } : {}
            } catch (e) {
                console.error('reCAPTCHA error', e)
                return {}
            }
        },
        [siteKey, executeRecaptcha]
    )

    return { enabled: !!siteKey, getRecaptchaHeaders }
}

/**
 * Wraps a component in a RecaptchaProvider, so it can call useRecaptcha().
 */
export const withRecaptcha = Component => {
    const WithRecaptcha = props => (
        <RecaptchaProvider>
            <Component {...props} />
        </RecaptchaProvider>
    )

    WithRecaptcha.displayName = `withRecaptcha(${Component.displayName ||
        Component.name ||
        'Component'})`

    return WithRecaptcha
}
